import { decamelize } from "humps";
import castArray from "lodash/castArray";
import isArray from "lodash/isArray";
import { isFilterValueOptionV2, isOptionsFilterV2 } from "PFCore/helpers/filters";
import { fetchAutocompleteCustomValues } from "PFCore/services/autocomplete/fetch_autocomplete_custom_values";
import { fetchFilterOptions, FetchFilterOptionsResponse } from "PFCore/services/filters/fetch_filter_options";
import { Id } from "PFTypes";
import {
  FilterOptionsQueryParams,
  FilterOptionV2,
  FilterTypeV2,
  FilterV2,
  FilterV2OptionsFetch,
  OptionsFilterV2
} from "PFTypes/filters_v2";

const FILTER_TYPES_SUPPORTING_OPTIONS_FETCHING = [
  FilterTypeV2.SelectMany,
  FilterTypeV2.SelectOne,
  FilterTypeV2.Checkboxes,
  FilterTypeV2.Radio,
  FilterTypeV2.Number
];

const getStaticOptionsQuery =
  (filter: OptionsFilterV2) =>
  ({ value }: FilterOptionsQueryParams) =>
    Promise.resolve({
      entries: (value
        ? filter.options.filter(({ id }) => (isArray(value) ? value.includes(id) : id === value))
        : filter.options) as FilterOptionV2[],
      meta: {
        total: filter.options.length
      }
    });

export const getFilterOptionsQuery = (
  filter: Pick<FilterV2, "optionsQuery" | "isChildren" | "name" | "type" | "segment">,
  searchId?: Id
): FilterV2OptionsFetch | null => {
  if (filter.optionsQuery) {
    return filter.optionsQuery;
  }

  if (isOptionsFilterV2(filter as FilterV2) && (filter as OptionsFilterV2).options.length > 0) {
    return getStaticOptionsQuery(filter as OptionsFilterV2);
  }

  if (!FILTER_TYPES_SUPPORTING_OPTIONS_FETCHING.includes(filter.type)) {
    return null;
  }

  const { isChildren, name, segment } = filter;

  if (segment) {
    return ({ term, value }) =>
      fetchAutocompleteCustomValues({
        type: decamelize(name),
        term,
        ...(value && {
          value: castArray(value).map((val) => String(isFilterValueOptionV2(val) ? val.id : val))
        })
      }).then((entries) => ({
        entries,
        meta: { total: entries.length }
      })) as Promise<FetchFilterOptionsResponse>;
  }

  if (!searchId) {
    return null;
  }

  return ({ term, value }: FilterOptionsQueryParams) =>
    fetchFilterOptions({
      searchId,
      name,
      term,
      value: value as string,
      isChildren
    });
};
